
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AVAILABILITY: Record<string, 'available' | 'booked' | 'checkin' | 'checkout'> = {
  // Janeiro 2025 - alguns dias ocupados
  '2025-07-01': 'booked',
  '2025-07-02': 'booked',
  '2025-07-03': 'checkout',
  '2025-07-07': 'checkin',
  '2025-07-08': 'booked',
  '2025-07-09': 'booked',
  '2025-07-10': 'booked',
  '2025-07-11': 'checkout',
  '2025-07-14': 'checkin',
  '2025-07-15': 'booked',
  '2025-07-16': 'booked',
  '2025-07-17': 'booked',
  '2025-07-18': 'booked',
  '2025-07-19': 'checkout',
  '2025-07-22': 'checkin',
  '2025-07-23': 'booked',
  '2025-07-24': 'booked',
  '2025-07-25': 'booked',
  '2025-07-26': 'checkout',
  '2025-08-01': 'checkin',
  '2025-08-02': 'booked',
  '2025-08-03': 'booked',
  '2025-08-04': 'booked',
  '2025-08-05': 'booked',
  '2025-08-06': 'booked',
  '2025-08-07': 'booked',
  '2025-08-08': 'checkout',
  '2025-08-11': 'checkin',
  '2025-08-12': 'booked',
  '2025-08-13': 'booked',
  '2025-08-14': 'booked',
  '2025-08-15': 'booked',
  '2025-08-16': 'checkout',
  '2025-08-20': 'checkin',
  '2025-08-21': 'booked',
  '2025-08-22': 'booked',
  '2025-08-23': 'booked',
  '2025-08-24': 'checkout',
  '2025-08-28': 'checkin',
  '2025-08-29': 'booked',
  '2025-08-30': 'booked',
  '2025-08-31': 'booked',
  '2025-09-05': 'checkin',
  '2025-09-06': 'booked',
  '2025-09-07': 'booked',
  '2025-09-08': 'checkout',
  '2025-09-15': 'checkin',
  '2025-09-16': 'booked',
  '2025-09-17': 'booked',
  '2025-09-18': 'booked',
  '2025-09-19': 'checkout',
};

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function toKey(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

interface MonthCalendarProps {
  year: number;
  month: number;
}

function MonthCalendar({ year, month }: MonthCalendarProps) {
  const today = new Date();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const cells: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="flex-1 min-w-[280px]">
      <h3 className="text-center text-base font-semibold text-gray-800 mb-3">
        {MONTH_NAMES[month]} {year}
      </h3>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAY_NAMES.map((d) => (
          <div key={d} className="text-center text-xs font-medium text-gray-500 py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const key = toKey(year, month, day);
          const status = AVAILABILITY[key];
          const isPast =
            new Date(year, month, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

          let cellClass =
            'relative h-9 w-full flex items-center justify-center rounded text-sm font-medium transition-all ';

          if (isPast) {
            cellClass += 'text-gray-300 cursor-default';
          } else if (status === 'booked') {
            cellClass += 'bg-rose-100 text-rose-400 line-through cursor-not-allowed';
          } else if (status === 'checkin') {
            cellClass += 'bg-gradient-to-r from-white to-rose-100 text-rose-500 cursor-not-allowed';
          } else if (status === 'checkout') {
            cellClass += 'bg-gradient-to-r from-rose-100 to-white text-rose-500 cursor-not-allowed';
          } else {
            cellClass += 'bg-teal-50 text-teal-700 hover:bg-teal-100 cursor-pointer font-semibold';
          }

          return (
            <div key={key} className={cellClass} title={
              isPast ? '' :
              status === 'booked' ? 'Ocupado' :
              status === 'checkin' ? 'Check-in' :
              status === 'checkout' ? 'Check-out' :
              'Disponível'
            }>
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AvailabilityBanner() {
  const navigate = useNavigate();
  const today = new Date();
  const [baseMonth, setBaseMonth] = useState<{ year: number; month: number }>({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  const secondMonth = baseMonth.month === 11
    ? { year: baseMonth.year + 1, month: 0 }
    : { year: baseMonth.year, month: baseMonth.month + 1 };

  const canGoPrev =
    baseMonth.year > today.getFullYear() ||
    (baseMonth.year === today.getFullYear() && baseMonth.month > today.getMonth());

  function prevMonth() {
    if (!canGoPrev) return;
    setBaseMonth((prev) =>
      prev.month === 0
        ? { year: prev.year - 1, month: 11 }
        : { year: prev.year, month: prev.month - 1 }
    );
  }

  function nextMonth() {
    setBaseMonth((prev) =>
      prev.month === 11
        ? { year: prev.year + 1, month: 0 }
        : { year: prev.year, month: prev.month + 1 }
    );
  }

  return (
    <section className="bg-white border-b border-gray-100 py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-1.5 rounded-full text-sm font-medium mb-3">
            <i className="ri-calendar-check-line"></i>
            Disponibilidade em Tempo Real
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Verifique as Datas Disponíveis
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">
            Consulte o calendário de disponibilidade e reserve o seu apartamento com antecedência.
          </p>
        </div>

        {/* Calendar Card */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8 max-w-4xl mx-auto">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={prevMonth}
              disabled={!canGoPrev}
              className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all cursor-pointer ${
                canGoPrev
                  ? 'border-gray-300 text-gray-700 hover:bg-gray-100'
                  : 'border-gray-100 text-gray-300 cursor-not-allowed'
              }`}
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <span className="text-sm text-gray-500 font-medium">
              {MONTH_NAMES[baseMonth.month]} — {MONTH_NAMES[secondMonth.month]} {secondMonth.year}
            </span>
            <button
              onClick={nextMonth}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>

          {/* Two Months */}
          <div className="flex flex-col md:flex-row gap-8">
            <MonthCalendar year={baseMonth.year} month={baseMonth.month} />
            <div className="hidden md:block w-px bg-gray-200 self-stretch"></div>
            <MonthCalendar year={secondMonth.year} month={secondMonth.month} />
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center justify-center gap-5 mt-6 pt-5 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-teal-50 border border-teal-200"></div>
              <span className="text-xs text-gray-600">Disponível</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-rose-100"></div>
              <span className="text-xs text-gray-600">Ocupado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-white to-rose-100 border border-gray-200"></div>
              <span className="text-xs text-gray-600">Check-in</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-rose-100 to-white border border-gray-200"></div>
              <span className="text-xs text-gray-600">Check-out</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-100"></div>
              <span className="text-xs text-gray-600">Passado</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></div>
            Calendário atualizado regularmente
          </div>
          <button
            onClick={() => navigate('/contactos')}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            <i className="ri-whatsapp-line"></i>
            Confirmar Disponibilidade via WhatsApp
          </button>
        </div>
      </div>
    </section>
  );
}
